import * as store from '../app/store.js';
import { Scenario } from '../domain/types.js';
import { ShareError, ShareErrorCode, decodeShareToken, extractShareToken } from '../features/persistence/share.js';
import { formatPercent, formatRub, formatTerm } from './format.js';
import { closeModal, openModal } from './share-dialog.js';

const PREVIEW_DEBOUNCE_MS = 150;
const EMPTY_TEXT = 'Вставьте ссылку или код целиком';
const ERROR_TEXT: Record<ShareErrorCode, string> = {
  corrupt: 'Код повреждён или неполный',
  'not-a-scenario': 'Это не сценарий калькулятора',
  'too-long': 'Код слишком длинный',
  unsupported: 'Ваш браузер не поддерживает импорт',
};

/** The preview line: «{название}: 3 000 000 ₽ под 9,5 % на 20 лет, событий: 1». */
export function previewText(scenario: Scenario): string {
  const events = scenario.ratePeriods.length + scenario.overpayments.length + scenario.refinances.length;
  return `${scenario.name}: ${formatRub(scenario.principal)} под ${formatPercent(scenario.rateApr)} на ${formatTerm(scenario.termMonths)}, событий: ${events}`;
}

export type ImportPreview = { scenario: Scenario | null; text: string };

/** Decodes pasted text into a preview line; never throws. */
export async function previewImport(text: string): Promise<ImportPreview> {
  const token = extractShareToken(text);
  if (!token) return { scenario: null, text: EMPTY_TEXT };
  try {
    const scenario = await decodeShareToken(token);
    return { scenario, text: previewText(scenario) };
  } catch (error) {
    return { scenario: null, text: ERROR_TEXT[error instanceof ShareError ? error.code : 'corrupt'] };
  }
}

/**
 * Wires `#import-scenario-btn` and `#import-dialog`: the textarea is previewed on input,
 * «Добавить в мои сценарии» upserts the decoded scenario (fresh id from the decoder),
 * closes the dialog and calls `onImported` (the drawer closes itself there).
 */
export function initImportDialog(onImported: () => void): void {
  const dialog = document.getElementById('import-dialog') as HTMLDialogElement | null;
  const trigger = document.getElementById('import-scenario-btn');
  const input = document.getElementById('import-input') as HTMLTextAreaElement | null;
  const preview = document.getElementById('import-preview');
  const confirm = document.getElementById('import-confirm') as HTMLButtonElement | null;
  const cancel = document.getElementById('import-cancel');
  if (!dialog || !trigger || !input || !preview || !confirm) return;
  if (dialog.dataset.bound === 'true') return;
  dialog.dataset.bound = 'true';

  let decoded: Scenario | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let request = 0;

  const show = (result: ImportPreview) => {
    decoded = result.scenario;
    preview.textContent = result.text;
    confirm.disabled = !decoded;
  };

  const refresh = () => {
    const current = ++request;
    void previewImport(input.value).then(result => {
      if (current === request) show(result);
    });
  };

  trigger.addEventListener('click', () => {
    input.value = '';
    if (timer) clearTimeout(timer);
    request++; // a pending preview from a previous session must not land in the fresh dialog
    show({ scenario: null, text: '' });
    openModal(dialog);
    input.focus();
  });
  input.addEventListener('input', () => {
    // The previous scenario must not be confirmable while the new text is being decoded.
    decoded = null;
    confirm.disabled = true;
    if (timer) clearTimeout(timer);
    timer = setTimeout(refresh, PREVIEW_DEBOUNCE_MS);
  });
  confirm.addEventListener('click', () => {
    if (!decoded) return;
    const { issues } = store.upsertScenario(decoded);
    if (issues.length > 0) {
      show({ scenario: null, text: ERROR_TEXT['not-a-scenario'] });
      return;
    }
    closeModal(dialog);
    onImported();
  });
  cancel?.addEventListener('click', () => closeModal(dialog));
}
